import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Users, UserPlus, TrendingUp, DollarSign, Send, 
  Phone, Calendar, ChevronRight, Copy, Check,
  AlertCircle, Clock, MessageSquare, Target, MessagesSquare,
  Settings, Shield, X
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { PotentialRevenueDashboard } from './PotentialRevenueDashboard';
import { ConversationsDashboard } from './ConversationsDashboard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Panel de Admin para Sistema de Upsell
 * Gestiona Sub-Admins, Dashboard de Potencial, y Mensajes de Upsell
 */
export const AdminUpsellPanel = ({ adminId }) => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language?.startsWith('es');
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  
  // SubAdmins
  const [subAdmins, setSubAdmins] = useState([]);
  const [showAddSubAdmin, setShowAddSubAdmin] = useState(false);
  const [newSubAdmin, setNewSubAdmin] = useState({ email: '', name: '', role: 'consultant' });
  const [createdPassword, setCreatedPassword] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState(null); // subadmin id being edited
  const [permissionsForm, setPermissionsForm] = useState({});
  const [savingPermissions, setSavingPermissions] = useState(false);
  
  // Pending messages
  const [pendingMessages, setPendingMessages] = useState([]);
  
  // Consultations
  const [consultations, setConsultations] = useState([]);
  
  // Selected business for detail view
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  const content = {
    es: {
      tabs: {
        dashboard: "Dashboard",
        conversations: "Conversaciones",
        subadmins: "Consultores",
        messages: "Mensajes",
        consultations: "Consultas"
      },
      dashboard: {
        title: "Oportunidades de Upsell",
        totalPotential: "Potencial Total",
        totalBusinesses: "Negocios con Potencial",
        pendingContact: "Pendiente Contacto",
        inConversation: "En Conversación",
        scheduled: "Consulta Programada",
        converted: "Convertidos",
        topOpportunities: "Top Oportunidades"
      },
      subadmins: {
        title: "Gestión de Consultores",
        add: "Añadir Consultor",
        email: "Email",
        name: "Nombre",
        role: "Rol",
        roles: {
          consultant: "Consultor",
          support: "Soporte",
          analyst: "Analista"
        },
        assigned: "Negocios Asignados",
        metrics: "Métricas",
        create: "Crear Consultor",
        passwordNote: "Guarda esta contraseña, solo se muestra una vez:",
        noSubadmins: "No hay consultores creados",
        permissions: "Permisos",
        editPermissions: "Editar Permisos",
        savePermissions: "Guardar",
        permissionLabels: {
          can_view_all_businesses: "Ver todos los negocios",
          can_send_upsell_messages: "Enviar mensajes de upsell",
          can_schedule_calls: "Programar llamadas",
          can_view_revenue_metrics: "Ver métricas de ingresos",
          can_access_webs_demo: "Acceso a panel Webs Demo"
        }
      },
      messages: {
        title: "Mensajes Pendientes",
        sendMock: "Enviar (Mock)",
        sendReal: "Enviar (Real)",
        noMessages: "No hay mensajes pendientes",
        template: "Plantilla",
        channel: "Canal",
        scheduledFor: "Programado para"
      },
      consultations: {
        title: "Consultas Programadas",
        pending: "Pendientes",
        confirmed: "Confirmadas",
        completed: "Completadas",
        confirm: "Confirmar",
        complete: "Marcar Completada",
        noShow: "No Asistió",
        noConsultations: "No hay consultas"
      }
    },
    en: {
      tabs: {
        dashboard: "Dashboard",
        conversations: "Conversations",
        subadmins: "Consultants",
        messages: "Messages",
        consultations: "Consultations"
      },
      dashboard: {
        title: "Upsell Opportunities",
        totalPotential: "Total Potential",
        totalBusinesses: "Businesses with Potential",
        pendingContact: "Pending Contact",
        inConversation: "In Conversation",
        scheduled: "Consultation Scheduled",
        converted: "Converted",
        topOpportunities: "Top Opportunities"
      },
      subadmins: {
        title: "Consultant Management",
        add: "Add Consultant",
        email: "Email",
        name: "Name",
        role: "Role",
        roles: {
          consultant: "Consultant",
          support: "Support",
          analyst: "Analyst"
        },
        assigned: "Assigned Businesses",
        metrics: "Metrics",
        create: "Create Consultant",
        passwordNote: "Save this password, it's only shown once:",
        noSubadmins: "No consultants created",
        permissions: "Permissions",
        editPermissions: "Edit Permissions",
        savePermissions: "Save",
        permissionLabels: {
          can_view_all_businesses: "View all businesses",
          can_send_upsell_messages: "Send upsell messages",
          can_schedule_calls: "Schedule calls",
          can_view_revenue_metrics: "View revenue metrics",
          can_access_webs_demo: "Access Webs Demo panel"
        }
      },
      messages: {
        title: "Pending Messages",
        sendMock: "Send (Mock)",
        sendReal: "Send (Real)",
        noMessages: "No pending messages",
        template: "Template",
        channel: "Channel",
        scheduledFor: "Scheduled for"
      },
      consultations: {
        title: "Scheduled Consultations",
        pending: "Pending",
        confirmed: "Confirmed",
        completed: "Completed",
        confirm: "Confirm",
        complete: "Mark Complete",
        noShow: "No Show",
        noConsultations: "No consultations"
      }
    }
  };

  const c = isSpanish ? content.es : content.en;

  useEffect(() => {
    fetchDashboard();
    fetchSubAdmins();
    fetchPendingMessages();
    fetchConsultations();
  }, [adminId]);

  const fetchDashboard = async () => {
    try {
      const { data } = await axios.get(`${API}/upsell/admin/dashboard`, {
        headers: { 'X-Admin-Id': adminId }
      });
      setDashboardData(data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubAdmins = async () => {
    try {
      const { data } = await axios.get(`${API}/upsell/subadmins`, {
        headers: { 'X-Admin-Id': adminId }
      });
      setSubAdmins(data.subadmins || []);
    } catch (err) {
      console.error('Error fetching subadmins:', err);
    }
  };

  const fetchPendingMessages = async () => {
    try {
      const { data } = await axios.get(`${API}/upsell/messages/pending`, {
        headers: { 'X-Admin-Id': adminId }
      });
      setPendingMessages(data.messages || []);
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  };

  const fetchConsultations = async () => {
    try {
      const { data } = await axios.get(`${API}/upsell/consultations`, {
        headers: { 'X-Admin-Id': adminId }
      });
      setConsultations(data.consultations || []);
    } catch (err) {
      console.error('Error fetching consultations:', err);
    }
  };

  const createSubAdmin = async () => {
    try {
      const { data } = await axios.post(`${API}/upsell/subadmins`, null, {
        params: newSubAdmin,
        headers: { 'X-Admin-Id': adminId }
      });
      
      if (data.success) {
        setCreatedPassword(data.temp_password);
        fetchSubAdmins();
        toast.success(isSpanish ? 'Consultor creado' : 'Consultant created');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error creating consultant');
    }
  };

  const startEditPermissions = (subadmin) => {
    setEditingPermissions(subadmin.id);
    setPermissionsForm({
      can_view_all_businesses: subadmin.permissions?.can_view_all_businesses || false,
      can_send_upsell_messages: subadmin.permissions?.can_send_upsell_messages !== false,
      can_schedule_calls: subadmin.permissions?.can_schedule_calls !== false,
      can_view_revenue_metrics: subadmin.permissions?.can_view_revenue_metrics !== false,
      can_access_webs_demo: subadmin.permissions?.can_access_webs_demo || false
    });
  };

  const savePermissions = async (subadminId) => {
    try {
      setSavingPermissions(true);
      const params = new URLSearchParams();
      Object.entries(permissionsForm).forEach(([key, value]) => {
        params.append(key, value.toString());
      });
      
      await axios.put(`${API}/upsell/subadmins/${subadminId}/permissions?${params}`, null, {
        headers: { 'X-Admin-Id': adminId }
      });
      
      toast.success(isSpanish ? 'Permisos actualizados' : 'Permissions updated');
      setEditingPermissions(null);
      fetchSubAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error updating permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  const sendMessage = async (messageId, useMock = true) => {
    try {
      const { data } = await axios.post(`${API}/upsell/messages/${messageId}/send`, null, {
        params: { use_mock: useMock },
        headers: { 'X-Admin-Id': adminId }
      });
      
      if (data.success) {
        toast.success(data.mock ? 'Mensaje enviado (MOCK)' : 'Mensaje enviado');
        fetchPendingMessages();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error sending message');
    }
  };

  const updateConsultationStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/upsell/consultations/${bookingId}/status`, null, {
        params: { status },
        headers: { 'X-Admin-Id': adminId }
      });
      
      toast.success(isSpanish ? 'Estado actualizado' : 'Status updated');
      fetchConsultations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error updating status');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const tabs = [
    { id: 'dashboard', label: c.tabs.dashboard, icon: TrendingUp },
    { id: 'conversations', label: c.tabs.conversations, icon: MessagesSquare },
    { id: 'subadmins', label: c.tabs.subadmins, icon: Users },
    { id: 'messages', label: c.tabs.messages, icon: MessageSquare, count: pendingMessages.length },
    { id: 'consultations', label: c.tabs.consultations, icon: Calendar, count: consultations.filter(c => c.status === 'pending').length }
  ];

  return (
    <div className="bg-gray-900 rounded-2xl overflow-hidden" data-testid="admin-upsell-panel">
      {/* Tabs */}
      <div className="flex border-b border-gray-700 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id 
                ? 'text-emerald-400 border-b-2 border-emerald-400' 
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.count > 0 && (
              <span className="bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="p-6">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-6">{c.dashboard.title}</h3>
            
            {loading ? (
              <div className="text-center py-8 text-gray-400">Loading...</div>
            ) : dashboardData ? (
              <>
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-800 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">{c.dashboard.totalPotential}</p>
                    <p className="text-2xl font-bold text-emerald-400">
                      {formatCurrency(dashboardData.total_potential_revenue)}
                    </p>
                  </div>
                  <div className="bg-gray-800 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">{c.dashboard.totalBusinesses}</p>
                    <p className="text-2xl font-bold text-white">{dashboardData.total_businesses}</p>
                  </div>
                  <div className="bg-gray-800 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">{c.dashboard.pendingContact}</p>
                    <p className="text-2xl font-bold text-amber-400">
                      {dashboardData.by_status?.pending_contact?.length || 0}
                    </p>
                  </div>
                  <div className="bg-gray-800 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">{c.dashboard.converted}</p>
                    <p className="text-2xl font-bold text-green-400">
                      {dashboardData.by_status?.converted?.length || 0}
                    </p>
                  </div>
                </div>

                {/* Top Opportunities */}
                <div className="bg-gray-800 rounded-xl p-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-4">{c.dashboard.topOpportunities}</h4>
                  <div className="space-y-2">
                    {dashboardData.top_opportunities?.map((opp, idx) => (
                      <div 
                        key={idx}
                        onClick={() => setSelectedBusiness(opp.business_id)}
                        className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg hover:bg-gray-700 cursor-pointer transition-colors"
                      >
                        <div>
                          <p className="text-white font-medium">{opp.business_name || 'Sin nombre'}</p>
                          <p className="text-xs text-gray-400">{opp.contacts_count} contactos</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-emerald-400 font-bold">
                            {formatCurrency(opp.potential_revenue)}
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-400">
                No hay datos disponibles
              </div>
            )}
          </div>
        )}

        {/* Conversations Tab */}
        {activeTab === 'conversations' && (
          <ConversationsDashboard adminId={adminId} />
        )}

        {/* SubAdmins Tab */}
        {activeTab === 'subadmins' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">{c.subadmins.title}</h3>
              <Button 
                onClick={() => setShowAddSubAdmin(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="add-subadmin-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {c.subadmins.add}
              </Button>
            </div>

            {/* Create SubAdmin Form */}
            {showAddSubAdmin && (
              <div className="bg-gray-800 rounded-xl p-4 mb-6">
                {createdPassword ? (
                  <div className="text-center">
                    <Check className="w-12 h-12 text-green-400 mx-auto mb-4" />
                    <p className="text-white mb-2">{c.subadmins.passwordNote}</p>
                    <div className="flex items-center justify-center gap-2 bg-gray-700 rounded-lg p-3">
                      <code className="text-emerald-400 font-mono">{createdPassword}</code>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(createdPassword);
                          toast.success('Copiado');
                        }}
                        className="text-gray-400 hover:text-white"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <Button 
                      onClick={() => { setShowAddSubAdmin(false); setCreatedPassword(null); setNewSubAdmin({ email: '', name: '', role: 'consultant' }); }}
                      className="mt-4"
                      variant="outline"
                    >
                      Cerrar
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <input
                        type="email"
                        placeholder={c.subadmins.email}
                        value={newSubAdmin.email}
                        onChange={(e) => setNewSubAdmin({ ...newSubAdmin, email: e.target.value })}
                        className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                        data-testid="subadmin-email"
                      />
                      <input
                        type="text"
                        placeholder={c.subadmins.name}
                        value={newSubAdmin.name}
                        onChange={(e) => setNewSubAdmin({ ...newSubAdmin, name: e.target.value })}
                        className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                        data-testid="subadmin-name"
                      />
                      <select
                        value={newSubAdmin.role}
                        onChange={(e) => setNewSubAdmin({ ...newSubAdmin, role: e.target.value })}
                        className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                        data-testid="subadmin-role"
                      >
                        <option value="consultant">{c.subadmins.roles.consultant}</option>
                        <option value="support">{c.subadmins.roles.support}</option>
                        <option value="analyst">{c.subadmins.roles.analyst}</option>
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={createSubAdmin} className="bg-emerald-600 hover:bg-emerald-700">
                        {c.subadmins.create}
                      </Button>
                      <Button onClick={() => setShowAddSubAdmin(false)} variant="outline">
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* SubAdmins List */}
            {subAdmins.length > 0 ? (
              <div className="space-y-3">
                {subAdmins.map(sub => (
                  <div key={sub.id} className="bg-gray-800 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium">{sub.name}</p>
                        <p className="text-sm text-gray-400 truncate">{sub.email}</p>
                        {/* Permission Badges */}
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {sub.permissions?.can_access_webs_demo && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-cyan-900/50 text-cyan-400">
                              🌐 Webs
                            </span>
                          )}
                          {sub.permissions?.can_view_revenue_metrics && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-900/50 text-amber-400">
                              📊 Métricas
                            </span>
                          )}
                          {sub.permissions?.can_send_upsell_messages && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-900/50 text-green-400">
                              💬 Upsell
                            </span>
                          )}
                          {sub.permissions?.can_schedule_calls && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-indigo-900/50 text-indigo-400">
                              📞 Llamadas
                            </span>
                          )}
                          {sub.permissions?.can_view_all_businesses && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-pink-900/50 text-pink-400">
                              👁️ Todos
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          sub.role === 'consultant' ? 'bg-emerald-900/50 text-emerald-400' :
                          sub.role === 'support' ? 'bg-blue-900/50 text-blue-400' :
                          'bg-purple-900/50 text-purple-400'
                        }`}>
                          {c.subadmins.roles[sub.role]}
                        </span>
                        <span className="text-sm text-gray-400 hidden md:inline">
                          {sub.assigned_businesses?.length || 0} {c.subadmins.assigned}
                        </span>
                        <Button
                          onClick={() => editingPermissions === sub.id ? setEditingPermissions(null) : startEditPermissions(sub)}
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          data-testid={`edit-permissions-${sub.id}`}
                        >
                          <Settings className="w-3 h-3 mr-1" />
                          {c.subadmins.permissions}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Permissions Editor */}
                    {editingPermissions === sub.id && (
                      <div className="mt-4 pt-4 border-t border-gray-700">
                        <div className="flex items-center gap-2 mb-4">
                          <Shield className="w-4 h-4 text-emerald-400" />
                          <span className="text-sm font-medium text-white">{c.subadmins.editPermissions}</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(c.subadmins.permissionLabels).map(([key, label]) => (
                            <label 
                              key={key} 
                              className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={permissionsForm[key] || false}
                                onChange={(e) => setPermissionsForm(prev => ({ ...prev, [key]: e.target.checked }))}
                                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-emerald-500 focus:ring-emerald-500"
                                data-testid={`permission-${key}`}
                              />
                              <span className="text-sm text-gray-300">{label}</span>
                            </label>
                          ))}
                        </div>
                        <div className="flex gap-2 mt-4">
                          <Button
                            onClick={() => savePermissions(sub.id)}
                            disabled={savingPermissions}
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid="save-permissions-btn"
                          >
                            {savingPermissions ? (
                              <Clock className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <Check className="w-3 h-3 mr-1" />
                            )}
                            {c.subadmins.savePermissions}
                          </Button>
                          <Button
                            onClick={() => setEditingPermissions(null)}
                            size="sm"
                            variant="outline"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                {c.subadmins.noSubadmins}
              </div>
            )}
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-6">{c.messages.title}</h3>
            
            {pendingMessages.length > 0 ? (
              <div className="space-y-3">
                {pendingMessages.map(msg => (
                  <div key={msg._id} className="bg-gray-800 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-white font-medium">{msg.business_name || 'Negocio'}</p>
                        <p className="text-sm text-gray-400">{msg.contact_phone}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        msg.channel === 'whatsapp' ? 'bg-green-900/50 text-green-400' :
                        'bg-blue-900/50 text-blue-400'
                      }`}>
                        {msg.channel}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 mb-3">
                      <span>{c.messages.template}: {msg.template_key}</span>
                      <span className="mx-2">•</span>
                      <span>{c.messages.scheduledFor}: {new Date(msg.scheduled_at).toLocaleString()}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => sendMessage(msg._id, true)}
                        size="sm"
                        variant="outline"
                        className="text-xs"
                      >
                        {c.messages.sendMock}
                      </Button>
                      <Button 
                        onClick={() => sendMessage(msg._id, false)}
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-xs"
                      >
                        <Send className="w-3 h-3 mr-1" />
                        {c.messages.sendReal}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                {c.messages.noMessages}
              </div>
            )}
          </div>
        )}

        {/* Consultations Tab */}
        {activeTab === 'consultations' && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-6">{c.consultations.title}</h3>
            
            {consultations.length > 0 ? (
              <div className="space-y-3">
                {consultations.map(cons => (
                  <div key={cons._id} className="bg-gray-800 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-white font-medium">{cons.business_name}</p>
                        <p className="text-sm text-gray-400">{cons.contact_name} • {cons.contact_phone}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        cons.status === 'pending' ? 'bg-amber-900/50 text-amber-400' :
                        cons.status === 'confirmed' ? 'bg-blue-900/50 text-blue-400' :
                        cons.status === 'completed' ? 'bg-green-900/50 text-green-400' :
                        'bg-red-900/50 text-red-400'
                      }`}>
                        {cons.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-400 mb-3">
                      <Calendar className="w-4 h-4" />
                      {cons.preferred_datetime}
                    </div>
                    {cons.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => updateConsultationStatus(cons._id, 'confirmed')}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-xs"
                        >
                          {c.consultations.confirm}
                        </Button>
                      </div>
                    )}
                    {cons.status === 'confirmed' && (
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => updateConsultationStatus(cons._id, 'completed')}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-xs"
                        >
                          {c.consultations.complete}
                        </Button>
                        <Button 
                          onClick={() => updateConsultationStatus(cons._id, 'no_show')}
                          size="sm"
                          variant="outline"
                          className="text-xs"
                        >
                          {c.consultations.noShow}
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                {c.consultations.noConsultations}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Business Detail Modal */}
      {selectedBusiness && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white">Detalle de Potencial</h3>
              <button 
                onClick={() => setSelectedBusiness(null)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <PotentialRevenueDashboard 
                businessId={selectedBusiness}
                adminMode={true}
                adminId={adminId}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUpsellPanel;
